import { formatItemProgression } from "../../ui/itemProgression.js";
import { renderBrandIcon } from "../../ui/BrandIcon.js";

export interface NinjaDetailsRealtimeState {
  id: string;
  name: string;
  appearanceId: string;
  hp: number;
  maxHp: number;
  chakra: number;
  maxChakra: number;
  alive: boolean;
  animation: string;
  tileColumn: number;
  tileRow: number;
}

export interface NinjaDetailsEquipment {
  slot: string;
  item: {
    name: string;
    iconPath?: string;
    rarity: string;
    rarityColor: string;
    tier: number;
    enhancementLevel: number;
    attributes: readonly {
      label: string;
      value: number;
      unit: "flat" | "percent";
    }[];
  } | null;
}

export interface NinjaDetailsTechnique {
  id: string;
  name: string;
  iconPath?: string;
  level: number;
}

export interface NinjaDetailsProfile {
  name: string;
  appearanceId: string;
  rarity: string;
  rarityColor: string;
  rank: string;
  level: number;
  lifeStatus?: string;
  attributes: Readonly<Record<string, number>>;
  equipment: readonly NinjaDetailsEquipment[];
  techniques: readonly NinjaDetailsTechnique[];
}

export interface NinjaDetailsDialogOptions {
  parent: HTMLElement;
  loadProfile: (ninjaId: string) => Promise<NinjaDetailsProfile>;
  renderPortrait: (appearanceId: string, name: string) => string;
  onCloseRequest: () => void;
}

type NinjaDetailsTab = "status" | "equipment" | "techniques";

const NINJA_DETAILS_TABS: readonly NinjaDetailsTab[] = [
  "status",
  "equipment",
  "techniques",
];

const ATTRIBUTE_LABELS: Readonly<Record<string, string>> = {
  physicalAttack: "Physical attack",
  techniqueAttack: "Technique attack",
  physicalDefense: "Physical defense",
  techniqueDefense: "Technique defense",
  precision: "Precision",
  evasion: "Evasion",
  critical: "Critical",
  criticalResistance: "Critical resistance",
  criticalDamage: "Critical damage",
  criticalDamageReduction: "Critical damage reduction",
  physicalPenetration: "Physical penetration",
  techniquePenetration: "Technique penetration",
  controlPower: "Control power",
  tenacity: "Tenacity",
  movementSpeedPercent: "Movement speed",
  attackSpeedPercent: "Attack speed",
};

function escapeHtml(value: unknown): string {
  return String(value ?? "").replace(
    /[&<>"']/g,
    (character) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        character
      ] ?? character,
  );
}

function formatValue(value: number): string {
  const rounded = Math.round(value);
  return Number.isFinite(rounded) ? rounded.toLocaleString() : "0";
}

function formatStatValue(key: string, value: number): string {
  return `${formatValue(value)}${key.endsWith("Percent") ? "%" : ""}`;
}

function formatLifeStatus(status: string | undefined, alive: boolean): string {
  if (!status) return alive ? "In the field" : "Incapacitated";
  const labels: Readonly<Record<string, string>> = {
    available: "Available",
    incapacitated: "Incapacitated",
    hospitalized: "Hospitalized",
    dismissed: "Dismissed",
  };
  return labels[status] ?? status.replaceAll("_", " ");
}

export class NinjaDetailsDialog {
  private readonly root: HTMLElement;
  private readonly options: NinjaDetailsDialogOptions;
  private closeHandler: () => void;
  private focusedNinjaId: string | undefined;
  private requestVersion = 0;
  private visible = false;
  private activeTab: NinjaDetailsTab = "status";

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (!this.visible || event.key !== "Escape") return;
    event.preventDefault();
    event.stopPropagation();
    this.closeHandler();
  };

  private readonly onDocumentPointerDown = (event: PointerEvent): void => {
    if (!this.visible) return;
    const target = event.target;
    if (!(target instanceof Node) || this.root.contains(target)) return;
    // Canvas clicks are game input. GameScene handles toggling a selected ninja
    // or issuing a move command, so they must not be consumed by the dialog.
    if (target instanceof Element && target.closest("#game-mount canvas"))
      return;
    this.closeHandler();
  };

  constructor(options: NinjaDetailsDialogOptions) {
    this.options = options;
    this.closeHandler = options.onCloseRequest;
    this.root = document.createElement("aside");
    this.root.className = "game-ninja-details";
    this.root.hidden = true;
    this.options.parent.append(this.root);
  }

  setCloseHandler(handler: () => void): void {
    this.closeHandler = handler;
  }

  setFocusedNinja(realtime: NinjaDetailsRealtimeState | null): void {
    if (!realtime) {
      this.hide();
      return;
    }

    const isNewNinja = realtime.id !== this.focusedNinjaId;
    this.focusedNinjaId = realtime.id;
    this.visible = true;
    this.root.hidden = false;
    if (isNewNinja) {
      this.activeTab = "status";
      const version = ++this.requestVersion;
      this.renderLoading(realtime);
      this.installDismissListeners();
      void this.options
        .loadProfile(realtime.id)
        .then((profile) => {
          if (
            version !== this.requestVersion ||
            realtime.id !== this.focusedNinjaId
          )
            return;
          this.renderProfile(profile, realtime);
        })
        .catch((error: unknown) => {
          if (version !== this.requestVersion) return;
          this.renderProfileError(realtime, error);
        });
      return;
    }

    this.updateRealtime(realtime);
  }

  dispose(): void {
    this.requestVersion += 1;
    this.removeDismissListeners();
    this.root.remove();
    this.visible = false;
    this.focusedNinjaId = undefined;
  }

  private hide(): void {
    if (!this.visible && this.root.hidden) return;
    this.requestVersion += 1;
    this.visible = false;
    this.focusedNinjaId = undefined;
    this.root.hidden = true;
    this.root.replaceChildren();
    this.removeDismissListeners();
  }

  private installDismissListeners(): void {
    window.addEventListener("keydown", this.onKeyDown, true);
    document.addEventListener("pointerdown", this.onDocumentPointerDown, true);
  }

  private removeDismissListeners(): void {
    window.removeEventListener("keydown", this.onKeyDown, true);
    document.removeEventListener(
      "pointerdown",
      this.onDocumentPointerDown,
      true,
    );
  }

  private requestClose(): void {
    this.closeHandler();
  }

  private renderLoading(realtime: NinjaDetailsRealtimeState): void {
    this.root.innerHTML = this.shellMarkup(
      realtime.name,
      realtime.appearanceId,
      "Loading ninja details…",
      realtime,
    );
    this.bindCloseButton();
    this.updateRealtime(realtime);
  }

  private renderProfile(
    profile: NinjaDetailsProfile,
    realtime: NinjaDetailsRealtimeState,
  ): void {
    const stats = Object.entries(profile.attributes)
      .filter(
        ([key, value]) =>
          Number.isFinite(value) && key !== "health" && key !== "chakra",
      )
      .sort(([left], [right]) => left.localeCompare(right))
      .map(
        ([key, value]) =>
          `<article><span>${escapeHtml(ATTRIBUTE_LABELS[key] ?? key)}</span><b>${escapeHtml(formatStatValue(key, value))}</b></article>`,
      )
      .join("");
    const equipment = profile.equipment
      .map(({ slot, item }) => {
        if (!item)
          return `<article class="game-ninja-equipment-slot is-empty"><span class="game-ninja-equipment-copy"><small>${escapeHtml(slot)}</small><b>Empty</b></span></article>`;
        const icon = item.iconPath
          ? `<img src="/${escapeHtml(item.iconPath)}" alt="" loading="lazy" />`
          : `<span class="game-ninja-equipment-fallback">${renderBrandIcon({ size: "small" })}</span>`;
        const itemStats = item.attributes
          .map(
            (attribute) =>
              `${escapeHtml(attribute.label)} +${escapeHtml(formatValue(attribute.value))}${attribute.unit === "percent" ? "%" : ""}`,
          )
          .join(" · ");
        const progression = formatItemProgression(
          item.tier,
          item.enhancementLevel,
        );
        const quality = [item.rarity, progression].filter(Boolean).join(" · ");
        return `<article class="game-ninja-equipment-slot"><span class="game-ninja-equipment-icon" style="--item-rarity:${escapeHtml(item.rarityColor)}">${icon}</span><span class="game-ninja-equipment-copy"><small>${escapeHtml(slot)}</small><b>${escapeHtml(item.name)}</b><span style="color:${escapeHtml(item.rarityColor)}">${escapeHtml(quality)}</span>${itemStats ? `<span>${itemStats}</span>` : ""}</span></article>`;
      })
      .join("");
    const techniques = profile.techniques.length
      ? profile.techniques
          .map((technique) => {
            const icon = technique.iconPath
              ? `<img src="/${escapeHtml(technique.iconPath)}" alt="" loading="lazy" />`
              : `<span class="game-ninja-technique-fallback">${renderBrandIcon({ size: "small" })}</span>`;
            return `<article class="game-ninja-technique">${icon}<span><b>${escapeHtml(technique.name)}</b><small>Level ${escapeHtml(technique.level)}</small></span></article>`;
          })
          .join("")
      : '<p class="game-ninja-details-empty">No jutsu equipped.</p>';

    const body = this.tabbedContentMarkup(
      profile,
      realtime,
      stats,
      equipment,
      techniques,
    );
    this.root.innerHTML = this.shellMarkup(
      profile.name,
      profile.appearanceId,
      body,
      realtime,
      profile,
    );
    this.bindCloseButton();
    this.updateRealtime(realtime);
  }

  private renderProfileError(
    realtime: NinjaDetailsRealtimeState,
    error: unknown,
  ): void {
    const message =
      error instanceof Error ? error.message : "Could not load ninja details.";
    this.root.innerHTML = this.shellMarkup(
      realtime.name,
      realtime.appearanceId,
      `<p class="game-ninja-details-error">${escapeHtml(message)}</p>`,
      realtime,
    );
    this.bindCloseButton();
    this.updateRealtime(realtime);
  }

  private shellMarkup(
    name: string,
    appearanceId: string,
    content: string,
    realtime: NinjaDetailsRealtimeState,
    profile?: NinjaDetailsProfile,
  ): string {
    const portrait = this.options.renderPortrait(appearanceId, name);
    const rarityColor = profile?.rarityColor ?? "#a9b3b7";
    const identity = profile
      ? `<p class="game-ninja-details-eyebrow" style="--ninja-rarity:${escapeHtml(rarityColor)}">${escapeHtml(profile.rarity)} · ${escapeHtml(profile.rank)}</p><h2 id="game-ninja-details-title">${escapeHtml(profile.name)}</h2><small>LEVEL ${escapeHtml(profile.level)}</small>`
      : `<p class="game-ninja-details-eyebrow">NINJA DETAILS</p><h2 id="game-ninja-details-title">${escapeHtml(name)}</h2><small>Loading profile…</small>`;
    return `<section class="game-ninja-details-card" role="dialog" aria-modal="false" aria-labelledby="game-ninja-details-title" tabindex="-1"><header class="game-ninja-details-header"><div class="game-ninja-details-portrait">${portrait}</div><div class="game-ninja-details-identity">${identity}</div><button type="button" class="game-ninja-details-close" aria-label="Close ninja details">×</button></header><div class="game-ninja-details-body"><section class="game-ninja-live-status"><div class="game-ninja-live-heading"><span data-ninja-live-state></span><span data-ninja-tile></span><span data-ninja-animation></span></div><div class="game-ninja-vital"><div><span>HP</span><b data-ninja-hp></b></div><div class="game-ninja-vital-track"><i data-ninja-hp-bar></i></div></div><div class="game-ninja-vital chakra"><div><span>CHAKRA</span><b data-ninja-chakra></b></div><div class="game-ninja-vital-track"><i data-ninja-chakra-bar></i></div></div></section><div class="game-ninja-details-content">${content}</div></div></section>`;
  }

  private bindCloseButton(): void {
    this.root
      .querySelector<HTMLButtonElement>(".game-ninja-details-close")
      ?.addEventListener("click", () => this.requestClose());
    this.bindTabs();
  }

  private tabbedContentMarkup(
    profile: NinjaDetailsProfile,
    realtime: NinjaDetailsRealtimeState,
    stats: string,
    equipment: string,
    techniques: string,
  ): string {
    const tabs: readonly { id: NinjaDetailsTab; label: string }[] = [
      { id: "status", label: "Status" },
      { id: "equipment", label: "Equipment" },
      { id: "techniques", label: "Jutsus" },
    ];
    const tabButtons = tabs
      .map(
        ({ id, label }) =>
          `<button id="game-ninja-tab-${id}" type="button" role="tab" data-ninja-tab="${id}" aria-controls="game-ninja-panel-${id}" aria-selected="${this.activeTab === id}" tabindex="${this.activeTab === id ? 0 : -1}">${label}</button>`,
      )
      .join("");
    const statusPanel = `<p class="game-ninja-details-status">${escapeHtml(formatLifeStatus(profile.lifeStatus, realtime.alive))}</p><details class="game-ninja-attributes-details"><summary>Attributes</summary><div class="game-ninja-attributes">${stats || '<p class="game-ninja-details-empty">No attributes available.</p>'}</div></details>`;
    const equipmentPanel = `<section class="game-ninja-details-section"><h3>Equipment</h3><div class="game-ninja-equipment">${equipment}</div></section>`;
    const techniquesPanel = `<section class="game-ninja-details-section"><h3>Equipped jutsu</h3><div class="game-ninja-techniques">${techniques}</div></section>`;
    const panels: Readonly<Record<NinjaDetailsTab, string>> = {
      status: statusPanel,
      equipment: equipmentPanel,
      techniques: techniquesPanel,
    };
    const tabPanels = NINJA_DETAILS_TABS.map(
      (id) =>
        `<section id="game-ninja-panel-${id}" class="game-ninja-tab-panel" role="tabpanel" aria-labelledby="game-ninja-tab-${id}" data-ninja-panel="${id}"${this.activeTab === id ? "" : " hidden"}>${panels[id]}</section>`,
    ).join("");

    return `<div class="game-ninja-details-tabs" role="tablist" aria-label="Ninja details">${tabButtons}</div>${tabPanels}`;
  }

  private bindTabs(): void {
    const tabList = this.root.querySelector<HTMLElement>(
      ".game-ninja-details-tabs",
    );
    if (!tabList) return;

    tabList.addEventListener("click", (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const button = target.closest<HTMLButtonElement>("[data-ninja-tab]");
      const tab = button?.dataset.ninjaTab;
      if (button && this.isDetailsTab(tab)) this.activateTab(tab);
    });
    tabList.addEventListener("keydown", (event: KeyboardEvent) => {
      const current = event.target;
      if (!(current instanceof HTMLButtonElement)) return;
      const currentTab = current.dataset.ninjaTab;
      if (!this.isDetailsTab(currentTab)) return;

      let nextIndex = NINJA_DETAILS_TABS.indexOf(currentTab);
      if (event.key === "ArrowRight") nextIndex += 1;
      else if (event.key === "ArrowLeft") nextIndex -= 1;
      else if (event.key === "Home") nextIndex = 0;
      else if (event.key === "End") nextIndex = NINJA_DETAILS_TABS.length - 1;
      else return;

      event.preventDefault();
      nextIndex =
        (nextIndex + NINJA_DETAILS_TABS.length) % NINJA_DETAILS_TABS.length;
      const nextTab = NINJA_DETAILS_TABS[nextIndex];
      if (!nextTab) return;
      this.activateTab(nextTab);
      this.root
        .querySelector<HTMLButtonElement>(`[data-ninja-tab="${nextTab}"]`)
        ?.focus();
    });
  }

  private isDetailsTab(value: string | undefined): value is NinjaDetailsTab {
    return value !== undefined && NINJA_DETAILS_TABS.includes(value as NinjaDetailsTab);
  }

  private activateTab(tab: NinjaDetailsTab): void {
    this.activeTab = tab;
    this.root.querySelectorAll<HTMLButtonElement>("[data-ninja-tab]").forEach(
      (button) => {
        const selected = button.dataset.ninjaTab === tab;
        button.setAttribute("aria-selected", String(selected));
        button.tabIndex = selected ? 0 : -1;
      },
    );
    this.root.querySelectorAll<HTMLElement>("[data-ninja-panel]").forEach(
      (panel) => {
        panel.hidden = panel.dataset.ninjaPanel !== tab;
      },
    );
  }

  private updateRealtime(realtime: NinjaDetailsRealtimeState): void {
    const root = this.root;
    const state = root.querySelector<HTMLElement>("[data-ninja-live-state]");
    const tile = root.querySelector<HTMLElement>("[data-ninja-tile]");
    const animation = root.querySelector<HTMLElement>("[data-ninja-animation]");
    const hp = root.querySelector<HTMLElement>("[data-ninja-hp]");
    const chakra = root.querySelector<HTMLElement>("[data-ninja-chakra]");
    const hpBar = root.querySelector<HTMLElement>("[data-ninja-hp-bar]");
    const chakraBar = root.querySelector<HTMLElement>(
      "[data-ninja-chakra-bar]",
    );
    if (state)
      state.textContent = realtime.alive ? "IN THE FIELD" : "INCAPACITATED";
    if (tile)
      tile.textContent = `Tile ${realtime.tileColumn}, ${realtime.tileRow}`;
    if (animation) animation.textContent = realtime.animation.toUpperCase();
    if (hp)
      hp.textContent = `${formatValue(realtime.hp)} / ${formatValue(realtime.maxHp)}`;
    if (chakra)
      chakra.textContent = `${formatValue(realtime.chakra)} / ${formatValue(realtime.maxChakra)}`;
    if (hpBar)
      hpBar.style.width = `${this.percentage(realtime.hp, realtime.maxHp)}%`;
    if (chakraBar)
      chakraBar.style.width = `${this.percentage(realtime.chakra, realtime.maxChakra)}%`;
  }

  private percentage(value: number, maximum: number): number {
    if (!Number.isFinite(value) || !Number.isFinite(maximum) || maximum <= 0)
      return 0;
    return Math.min(100, Math.max(0, (value / maximum) * 100));
  }
}
